/* eslint-disable no-nested-ternary */
import { useEffect, useState } from "react";

import { StoryQuestion, Profile } from "chq";

import { useDepartments, useLocations, useInterests, useSkills, useProfile, usePrograms, useDegrees, useClassYears, useClubs, useValues, useCompanies } from "app/context";
import { canViewCompaniesField, canViewSkillsField } from "app/permissions";

import tagsTypes, { getIcon } from "constants/tagsTypes";
import { getDepartmentFieldCategory, getCompanyFieldCategory } from "app/utils";

const makeSelectOptions = (entities: any, labelKey: string, valueKey: string, categoryIcon: string,
  icon: string, category: string, profileOptions: Array<any>, actionCallback?: any) => {
  const selectOptions: { label: string; value: string; categoryIcon: any; icon: any;
    category: string; actionButtonCallback: any; actionAriaLabel?: string; }[] = [];

  Object.keys(entities).forEach(entityId => {
    const { [labelKey]: label, [valueKey]: value } = entities[entityId];
    const option = {
      label,
      value,
      categoryIcon,
      icon,
      category,
      actionButtonCallback: undefined,
      actionAriaLabel: ""
    };
    if (actionCallback
      && profileOptions?.filter(profileOption => profileOption?.id === value).length === 0) {
      option.actionButtonCallback = actionCallback;
      option.actionAriaLabel = `Add ${label} - ${category}`;
    }
    selectOptions.push(option);
  });

  return selectOptions.sort((a, b) => a.label.localeCompare(b.label));
};

export const keyToCategory = (key: string) => {
  switch (key) {
    case "i":
      return { category: tagsTypes.INTERESTS, categoryIcon: getIcon(tagsTypes.INTERESTS) };
    case "co":
      return { category: tagsTypes.COMPANIES, categoryIcon: getIcon(tagsTypes.COMPANIES) };
    case "d":
      return { category: tagsTypes.DEPARTMENTS, categoryIcon: getIcon(tagsTypes.DEPARTMENTS) };
    case "l":
      return { category: tagsTypes.LOCATIONS, categoryIcon: getIcon(tagsTypes.LOCATIONS) };
    case "s":
      return { category: tagsTypes.SKILLS, categoryIcon: getIcon(tagsTypes.SKILLS) };
    case "cl":
      return { category: tagsTypes.CLUBS, categoryIcon: getIcon(tagsTypes.CLUBS) };
    case "cy":
      return { category: tagsTypes.CLASS_YEARS, categoryIcon: getIcon(tagsTypes.CLASS_YEARS) };
    case "p":
      return { category: tagsTypes.PROGRAMS, categoryIcon: getIcon(tagsTypes.PROGRAMS) };
    case "de":
      return { category: tagsTypes.DEGREES, categoryIcon: getIcon(tagsTypes.DEGREES) };
    case "v":
      return { category: tagsTypes.ORG_VALUES, categoryIcon: getIcon(tagsTypes.ORG_VALUES) };
    default:
      return {};
  }
};

export const useTagsFromQuery = (queryString: any = {}) => {
  const { profile: rawProfile } = useProfile();
  const { companies, onAllCompaniesRequested } = useCompanies();
  const { departments, onAllDepartmentsRequested } = useDepartments();
  const { interests, onAllInterestsRequested } = useInterests();
  const { locations, onAllLocationsRequested } = useLocations();
  const { skills, onAllSkillsRequested } = useSkills();
  const { programs, onAllProgramsRequested } = usePrograms();
  const { degrees, onAllDegreesRequested } = useDegrees();
  const { classYears, onAllClassYearsRequested } = useClassYears();
  const { clubs, onAllClubsRequested } = useClubs();
  const { organizationValues, onAllOrganizationValuesRequested } = useValues();
  const [valuesToReturn, setValuesToReturn] = useState<Array<any>>([]);
  const profile = rawProfile as Profile;

  useEffect(
    () => {
      if (profile && canViewCompaniesField(profile.organization)) {
        onAllCompaniesRequested();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]
  );

  useEffect(
    () => {
      if (profile && canViewSkillsField(profile.organization)) {
        onAllSkillsRequested();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]
  );

  useEffect(() => {
    onAllInterestsRequested();
    onAllLocationsRequested();
    onAllDepartmentsRequested();
    onAllProgramsRequested();
    onAllDegreesRequested();
    onAllClassYearsRequested();
    onAllClubsRequested();
    onAllOrganizationValuesRequested();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.entries(queryString).length === 0 && valuesToReturn.length === 0) {
      return;
    }

    const getTagsToSearch = (key: string) => {
      switch (key) {
        case "i":
          return interests;
        case "co":
          return companies;
        case "d":
          return departments;
        case "l":
          return locations;
        case "s":
          return skills;
        case "cl":
          return clubs;
        case "cy":
          return classYears;
        case "p":
          return programs;
        case "de":
          return degrees;
        case "v":
          return organizationValues;
        default:
          return {};
      }
    };

    const getTagLabel = (id: number, tags: any) => (tags[id]?.name);

    const values: Array<any> = [];
    Object.entries(queryString).forEach((element: Array<any>) => {
      const categories = keyToCategory(element[0]);
      const tags = getTagsToSearch(element[0]);
      if (Object.keys(tags).length !== 0) {
        element[1].forEach((id: number) => {
          values.push({
            value: id, label: getTagLabel(id, tags), ...categories
          });
        });
      }
    });
    setValuesToReturn(values);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, departments, interests, locations, skills, programs, degrees,
    classYears, clubs, organizationValues, queryString]);

  return valuesToReturn;
};

const getStoryQuestionsOptions = (storyQuestions: Array<StoryQuestion>) => (
  storyQuestions.map(({ id, question, storyQuestionType }) => ({
    label: question,
    value: id,
    icon: "book-filled",
    categoryIcon: "book",
    category: storyQuestionType === "parent_prompt" ? tagsTypes.STORY_TRENDS : tagsTypes.STORY_PROMPTS
  }))
);

const useTags = (
  optionsCallback: any,
  withOrgValues: boolean,
  wholeCompany: boolean,
  storyQuestions: Array<StoryQuestion> = [],
  newOptions: Array<any> = []
) => {
  const { profile: rawProfile } = useProfile();
  const { companies, onAllCompaniesRequested } = useCompanies();
  const { departments, onAllDepartmentsRequested } = useDepartments();
  const { interests, onAllInterestsRequested } = useInterests();
  const { locations, onAllLocationsRequested } = useLocations();
  const { skills, onAllSkillsRequested } = useSkills();
  const { programs, onAllProgramsRequested } = usePrograms();
  const { degrees, onAllDegreesRequested } = useDegrees();
  const { classYears, onAllClassYearsRequested } = useClassYears();
  const { clubs, onAllClubsRequested } = useClubs();
  const { organizationValues, onAllOrganizationValuesRequested } = useValues();
  const profile = rawProfile as Profile;

  useEffect(
    () => {
      if (profile && canViewCompaniesField(profile.organization)) {
        onAllCompaniesRequested();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]
  );

  useEffect(
    () => {
      if (profile && canViewSkillsField(profile.organization)) {
        onAllSkillsRequested();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]
  );

  useEffect(() => {
    onAllInterestsRequested();
    onAllLocationsRequested();
    onAllDepartmentsRequested();
    onAllProgramsRequested();
    onAllDegreesRequested();
    onAllClassYearsRequested();
    onAllClubsRequested();
    onAllOrganizationValuesRequested();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interestsOptions = makeSelectOptions(interests, "name", "id", "heart-ol", "heart", tagsTypes.INTERESTS, profile.interests, optionsCallback);
  const locationOptions = makeSelectOptions(locations, "name", "id", "loc-ol", "ios-location", tagsTypes.LOCATIONS, [profile.location], optionsCallback);
  const departmentOptions = makeSelectOptions(departments, "name", "id", "ios-people-outline", "ios-people", getDepartmentFieldCategory(rawProfile.organization.name), profile.departments, optionsCallback);
  const companyOptions = makeSelectOptions(companies, "name", "id", "building", "building-filled", getCompanyFieldCategory(rawProfile.organization.name), profile.companies, optionsCallback);
  const skillOptions = makeSelectOptions(skills, "name", "id", "hammer", "hammer-filled", tagsTypes.SKILLS, profile.skills, optionsCallback);
  const programOptions = makeSelectOptions(programs, "name", "id", "ios-star-outline", "star", tagsTypes.PROGRAMS, profile.programs, optionsCallback);
  const degreeOptions = makeSelectOptions(degrees, "name", "id", "mortarboard", "mortarboard-filled", tagsTypes.DEGREES, [profile.degree], optionsCallback);
  const clubOptions = makeSelectOptions(clubs, "name", "id", "ios-people-outline", "ios-people", tagsTypes.CLUBS, profile.clubs, optionsCallback);
  const classYearOptions = rawProfile.organization.name === "Dartmouth Guarini"
    ? makeSelectOptions(classYears, "name", "id", "clock", "clock-filled", tagsTypes.YEARS_IN_PROGRAM, [profile.classYear], optionsCallback)
    : makeSelectOptions(classYears, "name", "id", "clock", "clock-filled", tagsTypes.CLASS_YEARS, [profile.classYear], optionsCallback);
  const orgValuesOptions = makeSelectOptions(organizationValues, "name", "id", "diamond", "diamond-filled", tagsTypes.ORG_VALUES, [], undefined);
  const organization = {
    label: profile.organization.name,
    value: profile.organization.id,
    categoryIcon: "building",
    icon: "building-filled",
    category: tagsTypes.ALL_ORGANIZATION
  };

  let options: Array<any> = [];

  if (wholeCompany) {
    options = [organization];
  }

  if (withOrgValues) {
    options = [...options, ...orgValuesOptions];
  }

  options = [...options, ...interestsOptions, ...locationOptions];

  if (profile.organization?.isEducational) {
    options = [
      ...options,
      ...programOptions,
      ...degreeOptions,
      ...clubOptions,
      ...classYearOptions
    ];
  } else {
    options = [...options, ...departmentOptions];
  }

  if (canViewCompaniesField(profile.organization)) {
    options = [...options, ...companyOptions];
  }

  if (canViewSkillsField(profile.organization) && !profile.organization?.isEducational) {
    options = [...options, ...skillOptions];
  }

  if (storyQuestions.length > 0) {
    options = [
      ...options,
      ...getStoryQuestionsOptions(storyQuestions)
    ];
  }

  let indexes: any = {
    departments,
    interests,
    locations,
    skills,
    programs,
    degrees,
    clubs,
    classYears,
    organizationValues,
    organizations: {},
    storyPrompts: Object.fromEntries(storyQuestions.map(element => [element.id, element])),
    "organization values": organizationValues
  };

  indexes = { ...indexes, companies };

  const camelize = (str: string) => str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (
    index === 0 ? word.toLowerCase() : word.toUpperCase()
  )).replace(/\s+/g, "");

  const isTagEnabled = (tag: any): boolean => (!!indexes[camelize(tag.type)][tag.entityId]);
  options = [...options, ...newOptions];

  return {
    options,
    indexes,
    isTagEnabled
  };
};

export default useTags;
